import { Fragment, useEffect, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, Elektrouebersicht, Elektroseite, Elektrobauteil,
  Elektroanlagenknoten, Elektrobauteilart,
} from '../api';
import { IconEbene, IconRaum, IconSuche, IconWarnung } from '../Icons';
import { Fachaktionen } from './Fachaktionen';
import { symbolFuer, farbeFuer } from '../ElektroSymbole';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';

interface Rahmen { api: ApiClient; projekt?: { id: string } }

/** Welche Sicht der Baum zeigt. */
type Sicht = 'anlage' | 'raum' | 'verteiler' | 'typ';

interface Auswahl {
  art: Sicht;
  schluessel: string;
  name: string;
  filter: Bauteilfilter;
}

interface Bauteilfilter {
  bauteilart?: string;
  kostengruppe?: string;
  raum?: string;
  geschoss?: string;
  verteilerId?: string;
  stromkreisId?: string;
  typennummer?: string;
}

/**
 * Das Gewerk Elektro — Baumansicht.
 *
 * --- Warum vier Sichten ---------------------------------------------------------
 *
 * Vier verschiedene Fragen werden an denselben Bestand gestellt:
 *
 *   Anlage      Der Elektroplaner: Wie viele Notlichtleuchten hat das Projekt?
 *   Raum        Die Bauleitung: Was ist in Raum E1-042 zu montieren?
 *   Verteiler   Der Prüfer: Was hängt an UV-E1-01, Abgang F3?
 *   Typennummer Die Ausschreibung: Wie viele EE1.3 sind auszuschreiben?
 *
 * Eine gemeinsame Sicht müsste allen vier gerecht werden und wäre keiner gerecht.
 * Vier Umschalter sind billiger als ein Kompromiss.
 *
 * --- Zur Gliederung nach DIN 276 -------------------------------------------------
 *
 * Die Anlagensicht folgt der Kostengruppe, nicht den Revit-Kategorien. Die stammen
 * von zwei Herstellern und mischen sich; die Kostengruppe ist die Ordnung, in der
 * ohnehin ausgeschrieben und abgerechnet wird.
 */
export function ElektroBaum() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [sicht, setSicht] = useState<Sicht>('anlage');
  const [uebersicht, setUebersicht] = useState<Elektrouebersicht | null>(null);

  /**
   * Die Baumdaten MIT ihrer Sicht.
   *
   * Getrennt gehalten wäre es ein Fehler: Beim Umschalten steht `sicht` sofort auf
   * dem neuen Wert, `baum` aber noch auf den alten Daten. Die Anzeige liest dann
   * ein Feld als Objekt — `daten.wurzeln` ist undefined, und die Seite bricht mit
   * einem weißen Bildschirm ab.
   *
   * Genau das ist passiert. Mit der Sicht am Datensatz wird erst gezeichnet, wenn
   * beide zusammenpassen.
   */
  const [baum, setBaum] = useState<{ sicht: Sicht; daten: unknown } | null>(null);
  const [arten, setArten] = useState<Record<string, Elektrobauteilart>>({});
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    let abgebrochen = false;

    (async () => {
      try {
        const [u, b, a] = await Promise.all([
          api.elektroUebersicht(projekt.id),
          api.elektroBaum(projekt.id, sicht),
          api.elektroBauteilarten(),
        ]);

        if (abgebrochen) return;

        setUebersicht(u);
        setBaum({ sicht, daten: b });
        setArten(Object.fromEntries(a.map((x) => [x.schluessel, x])));
        setFehler(null);
      } catch (e) {
        if (!abgebrochen) setFehler(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => { abgebrochen = true; };
  }, [projekt?.id, sicht]);

  if (!projekt) return <div className="leer">wird geladen …</div>;
  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!uebersicht) return <div className="leer">wird geladen …</div>;

  return (
    <>
      {/* Lesen und Schreiben.
          Dieselbe Komponente wie bei Raumbuch, Türbuch, Lüftung und Heizung — sie
          kennt das Quellmodell der Rolle, das freigegebene Profil und die
          Rückfrage vor dem Schreiben. Eine eigene wäre eine zweite Wahrheit. */}
      <Fachaktionen
        api={api}
        rolleKey="elektro"
        leseArt={60}
        schreibArt={61}
        leseText="Aus Modell lesen"
        schreibText="Ins Modell schreiben"
      >
        {/* Die acht Verweise auf die Unterseiten standen frueher hier als
            Knopfreihe. Sie stehen jetzt im linken Gliederungsbaum des
            Elektrorahmens (seiten/elektro/abschnitte.ts): eine Reihe waechst
            mit jedem Paket in die Breite, eine Gliederung waechst nach unten
            und sagt ausserdem, was zusammengehoert. In der Werkzeugleiste
            bleibt, was eine HANDLUNG ist und keine Navigation. */}
        <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="elektro" />

      </Fachaktionen>

      <Kennzahlen u={uebersicht} projektId={projekt.id} />

      <div className="karte">
        {/* --- Die Sichtwahl ------------------------------------------------- */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12,
                      flexWrap: 'wrap', alignItems: 'center' }}>
          {([
            ['anlage', 'nach Anlage', 'DIN 276 — für die Fachplanung'],
            ['raum', 'nach Raum', 'Geschoss und Raum — für die Bauleitung'],
            ['verteiler', 'nach Verteiler', 'Verteiler und Abgang — für die Prüfung'],
            ['typ', 'nach Typennummer', 'EE1.3 — für die Ausschreibung'],
          ] as [Sicht, string, string][]).map(([s, text, hinweis]) => (
            <button
              key={s}
              onClick={() => { setSicht(s); setAuswahl(null); }}
              className={sicht === s ? 'betont' : undefined}
              style={{ fontSize: 12 }}
              title={hinweis}
            >
              {text}
            </button>
          ))}

          <span style={{ flex: 1 }} />

          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconSuche size={13} />
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="im Baum suchen"
              style={{ fontSize: 12, width: 180 }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* --- Der Baum -------------------------------------------------- */}
          <div style={{ width: 340, flexShrink: 0, maxHeight: '70vh',
                        overflow: 'auto',
                        borderRight: '1px solid var(--linie, #eee)',
                        paddingRight: 12 }}>
            {baum === null || baum.sicht !== sicht ? (
              <div className="leer">wird geladen …</div>
            ) : (
              <Baum
                sicht={sicht}
                daten={baum.daten}
                suche={suche}
                auswahl={auswahl}
                waehlen={setAuswahl}
              />
            )}
          </div>

          {/* --- Der Inhalt ------------------------------------------------ */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {auswahl ? (
              <Inhalt
                api={api}
                projektId={projekt.id}
                auswahl={auswahl}
                arten={arten}
              />
            ) : (
              <div className="leer">
                Links einen Zweig wählen. Der Baum zeigt
                {sicht === 'anlage' && ' die Anlagengruppen nach DIN 276.'}
                {sicht === 'raum' && ' Geschosse und Räume.'}
                {sicht === 'verteiler' && ' die Verteiler mit ihren Abgängen.'}
                {sicht === 'typ' && ' die Typennummern EE1 und EE2.'}
              </div>
            )}
          </div>
        </div>
      </div>

      <Legende />
    </>
  );
}

// === Die Kennzahlen ==========================================================

function Kennzahlen({
  u, projektId,
}: { u: Elektrouebersicht; projektId: string }) {
  const ohneKreis = u.gesamt - u.mitVerteiler;

  return (
    <div className="karte">
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
        <Zahl name="Bauteile" wert={u.gesamt} />
        <Zahl name="mit Raum" wert={u.mitRaum}
              anteil={u.gesamt ? u.mitRaum / u.gesamt : 0} />
        <Zahl name="mit Verteiler" wert={u.mitVerteiler}
              anteil={u.gesamt ? u.mitVerteiler / u.gesamt : 0} />
        <Zahl name="mit Abgang" wert={u.mitKreis}
              anteil={u.gesamt ? u.mitKreis / u.gesamt : 0} />
        <Zahl name="Verteiler" wert={u.verteiler} />
        <Zahl name="Abgänge" wert={u.stromkreise} />
      </div>

      {/* Die Zuordnung ist der Kern des Moduls. Fehlt sie bei mehr als einem
          Zehntel, gehört das ausdrücklich gesagt — sonst sieht die Übersicht
          vollständig aus, und die Verteilkreise sind leer. */}
      {ohneKreis > u.gesamt / 10 && (
        <div className="hinweis" style={{ marginTop: 12 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 12 }}>
            <strong>{ohneKreis}</strong> Bauteil(e) haben keinen Verteiler. Die
            Zuordnung läuft über den Raum und dessen Verteilerbereich im Raumbuch.
            {' '}
            <Link to={`/projekte/${projektId}/elektro-verteiler`}>
              Zur Verteilerpflege
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Zahl({
  name, wert, anteil,
}: { name: string; wert: number; anteil?: number }) {
  return (
    <div>
      <div className="feldname">{name}</div>
      <div style={{ fontSize: 19, fontWeight: 300 }}>
        {wert.toLocaleString('de-DE')}
        {anteil !== undefined && wert > 0 && (
          <span className="sehr-schwach" style={{ fontSize: 11, marginLeft: 5 }}>
            {Math.round(anteil * 100)} %
          </span>
        )}
      </div>
    </div>
  );
}

// === Der Baum ================================================================

function Baum({
  sicht, daten, suche, auswahl, waehlen,
}: {
  sicht: Sicht;
  daten: unknown;
  suche: string;
  auswahl: Auswahl | null;
  waehlen: (a: Auswahl) => void;
}) {
  switch (sicht) {
    case 'anlage':
      return (
        <AnlagenBaum
          knoten={daten as Elektroanlagenknoten[]}
          suche={suche}
          auswahl={auswahl}
          waehlen={waehlen}
          tiefe={0}
        />
      );

    case 'raum':
      return (
        <RaumBaum
          daten={daten as RaumKnoten[]}
          suche={suche}
          auswahl={auswahl}
          waehlen={waehlen}
        />
      );

    case 'verteiler':
      return (
        <VerteilerBaum
          daten={daten as VerteilerAntwort}
          suche={suche}
          auswahl={auswahl}
          waehlen={waehlen}
        />
      );

    case 'typ':
      return (
        <TypBaum
          daten={daten as TypKnoten[]}
          suche={suche}
          auswahl={auswahl}
          waehlen={waehlen}
        />
      );
  }
}

// --- Die Anlagensicht -------------------------------------------------------

function AnlagenBaum({
  knoten, suche, auswahl, waehlen, tiefe,
}: {
  knoten: Elektroanlagenknoten[];
  suche: string;
  auswahl: Auswahl | null;
  waehlen: (a: Auswahl) => void;
  tiefe: number;
}) {
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const passt = (k: Elektroanlagenknoten): boolean => {
    if (!suche.trim()) return true;

    const s = suche.toLowerCase();

    return k.name.toLowerCase().includes(s)
        || k.arten.some((a) => a.bezeichnung.toLowerCase().includes(s))
        || k.kinder.some(passt);
  };

  return (
    <>
      {(knoten ?? []).filter(passt).map((k) => {
        const auf = offen.has(k.schluessel) || suche.trim().length > 0;

        return (
          <Fragment key={k.schluessel}>
            <div
              className="baumzeile"
              style={{ paddingLeft: 4 + tiefe * 14, cursor: 'pointer',
                       display: 'flex', gap: 6, alignItems: 'center',
                       padding: '4px 4px' }}
              onClick={() => {
                const n = new Set(offen);
                auf ? n.delete(k.schluessel) : n.add(k.schluessel);
                setOffen(n);
              }}
              title={k.hinweis ?? undefined}
            >
              <span className="sehr-schwach" style={{ fontSize: 10, width: 9 }}>
                {k.kinder.length > 0 || k.arten.length > 0
                  ? (auf ? '−' : '+') : ''}
              </span>

              <span style={{ fontSize: 12, fontWeight: tiefe === 0 ? 500 : 400 }}>
                {k.name}
              </span>

              <span style={{ flex: 1 }} />

              <span className="schwach" style={{ fontSize: 11 }}>
                {k.anzahl.toLocaleString('de-DE')}
              </span>
            </div>

            {auf && (
              <>
                {k.arten.map((a) => (
                  <div
                    key={a.bauteilart}
                    className="baumzeile"
                    style={{
                      paddingLeft: 4 + (tiefe + 1) * 14 + 9,
                      cursor: 'pointer', display: 'flex', gap: 6,
                      alignItems: 'center', padding: '3px 4px',
                      background: auswahl?.schluessel === a.bauteilart
                        ? 'var(--flaeche, #f0f0f0)' : undefined,
                    }}
                    onClick={() => waehlen({
                      art: 'anlage',
                      schluessel: a.bauteilart,
                      name: a.bezeichnung,
                      filter: { bauteilart: a.bauteilart },
                    })}
                  >
                    {(() => {
                      const S = symbolFuer(a.bauteilart);
                      return <S size={14} />;
                    })()}
                    <span style={{ fontSize: 12 }}>{a.bezeichnung}</span>

                    <span style={{ flex: 1 }} />

                    {a.ohneKreis > 0 && (
                      <span className="sehr-schwach" style={{ fontSize: 9 }}
                            title={`${a.ohneKreis} ohne Verteiler`}>
                        {a.ohneKreis} offen
                      </span>
                    )}

                    <span className="schwach" style={{ fontSize: 11 }}>
                      {a.anzahl.toLocaleString('de-DE')}
                    </span>
                  </div>
                ))}

                {k.kinder.length > 0 && (
                  <AnlagenBaum
                    knoten={k.kinder}
                    suche={suche}
                    auswahl={auswahl}
                    waehlen={waehlen}
                    tiefe={tiefe + 1}
                  />
                )}
              </>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

// --- Die Raumsicht ----------------------------------------------------------

interface RaumKnoten {
  schluessel: string;
  name: string;
  anzahl: number;
  kinder: {
    schluessel: string;
    name: string;
    anzahl: number;
    ohneKreis: number;
    arten: { bauteilart: string; bezeichnung: string; anzahl: number }[];
  }[];
}

function RaumBaum({
  daten, suche, auswahl, waehlen,
}: {
  daten: RaumKnoten[];
  suche: string;
  auswahl: Auswahl | null;
  waehlen: (a: Auswahl) => void;
}) {
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const s = suche.toLowerCase();

  return (
    <>
      {(daten ?? []).map((g) => {
        const raeume = suche.trim()
          ? g.kinder.filter((r) => r.name.toLowerCase().includes(s))
          : g.kinder;

        if (suche.trim() && raeume.length === 0) return null;

        const auf = offen.has(g.schluessel) || suche.trim().length > 0;

        return (
          <Fragment key={g.schluessel}>
            <div
              className="baumzeile"
              style={{ display: 'flex', gap: 6, alignItems: 'center',
                       padding: '4px', cursor: 'pointer' }}
              onClick={() => {
                const n = new Set(offen);
                auf ? n.delete(g.schluessel) : n.add(g.schluessel);
                setOffen(n);
              }}
            >
              <span className="sehr-schwach" style={{ fontSize: 10, width: 9 }}>
                {auf ? '−' : '+'}
              </span>
              <IconEbene size={12} />
              <span style={{ fontSize: 12, fontWeight: 500 }}>{g.name}</span>
              <span style={{ flex: 1 }} />
              <span className="schwach" style={{ fontSize: 11 }}>
                {g.anzahl.toLocaleString('de-DE')}
              </span>
            </div>

            {auf && raeume.map((r) => (
              <div
                key={r.schluessel}
                className="baumzeile"
                style={{
                  paddingLeft: 27, display: 'flex', gap: 6, alignItems: 'center',
                  padding: '3px 4px 3px 27px', cursor: 'pointer',
                  background: auswahl?.schluessel === r.schluessel
                    ? 'var(--flaeche, #f0f0f0)' : undefined,
                }}
                onClick={() => waehlen({
                  art: 'raum',
                  schluessel: r.schluessel,
                  name: r.name,
                  filter: { raum: r.schluessel, geschoss: g.schluessel },
                })}
                title={r.arten
                  .map((a) => `${a.bezeichnung}: ${a.anzahl}`)
                  .join('\n')}
              >
                <IconRaum size={11} />
                <span style={{ fontSize: 12 }}>{r.name}</span>
                <span style={{ flex: 1 }} />

                {r.ohneKreis > 0 && (
                  <span className="sehr-schwach" style={{ fontSize: 9 }}
                        title={`${r.ohneKreis} ohne Verteiler`}>
                    {r.ohneKreis}
                  </span>
                )}

                <span className="schwach" style={{ fontSize: 11 }}>
                  {r.anzahl}
                </span>
              </div>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

// --- Die Verteilersicht -----------------------------------------------------

/**
 * Ein Verteiler in der Baumsicht.
 *
 * Die Ordnung folgt dem Adressschema: UV_2.0.7 heißt Quadrant 2, Geschoss G00,
 * Unterverteilung 7. Der Baum gliedert entsprechend — so ist die Anlage adressiert,
 * und so sucht jemand, der einen Verteiler finden will.
 */
interface VerteilerKnoten {
  id: string;
  schluessel: string;
  name: string;
  bezeichnung: string | null;
  art: string;
  verteilerbereich: string | null;
  raumnummer: string | null;
  quadrant: string | null;
  geschoss: string | null;
  verteilernummer: string | null;
  /** Woran er hängt, wenn eine Einspeisung gepflegt ist. */
  einspeisung: string | null;
  anzahl: number;
  /**
   * Quadranten, die die Bauteile aus der Geometrie mitbringen und die nicht zum
   * Bereichsnamen passen. Jeder Eintrag ist ein Hinweis auf eine falsche Zuordnung.
   */
  fremdeQuadranten: string[];
  ohneKreis: number;
  kreise: {
    id: string; schluessel: string; name: string;
    verwendung: string | null; schutzorgan: string | null;
    istSicherheit: boolean; anzahl: number;
  }[];
}

interface VerteilerAntwort {
  quadranten: {
    schluessel: string;
    name: string;
    anzahl: number;
    geschosse: {
      schluessel: string;
      name: string;
      anzahl: number;
      verteiler: VerteilerKnoten[];
    }[];
  }[];
  ohneVerteiler: number;
}

function VerteilerBaum({
  daten, suche, auswahl, waehlen,
}: {
  daten: VerteilerAntwort;
  suche: string;
  auswahl: Auswahl | null;
  waehlen: (a: Auswahl) => void;
}) {
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const umschalten = (k: string) => {
    const n = new Set(offen);
    n.has(k) ? n.delete(k) : n.add(k);
    setOffen(n);
  };

  const s = suche.toLowerCase();

  const passt = (v: VerteilerKnoten) =>
    !suche.trim()
    || v.schluessel.toLowerCase().includes(s)
    || (v.bezeichnung ?? '').toLowerCase().includes(s)
    || (v.raumnummer ?? '').toLowerCase().includes(s);

  const quadranten = daten?.quadranten ?? [];

  return (
    <>
      {quadranten.length === 0 && (
        <div className="leer" style={{ fontSize: 12 }}>
          Noch kein Verteiler angelegt.
        </div>
      )}

      {quadranten.map((qd) => {
        // Nur Geschosse, in denen etwas die Suche trifft.
        const geschosse = qd.geschosse
          .map((g) => ({ ...g, verteiler: g.verteiler.filter(passt) }))
          .filter((g) => g.verteiler.length > 0);

        if (geschosse.length === 0) return null;

        const qdAuf = offen.has(`q:${qd.schluessel}`) || suche.trim().length > 0;

        return (
          <Fragment key={qd.schluessel}>
            {/* --- Quadrant ------------------------------------------------- */}
            <div
              className="baumzeile"
              style={{ display: 'flex', gap: 6, alignItems: 'center',
                       padding: '5px 4px', cursor: 'pointer',
                       fontWeight: 500 }}
              onClick={() => umschalten(`q:${qd.schluessel}`)}
            >
              <span className="sehr-schwach" style={{ fontSize: 10, width: 9 }}>
                {qdAuf ? '−' : '+'}
              </span>
              <span style={{ fontSize: 12 }}>{qd.name}</span>
              <span style={{ flex: 1 }} />
              <span className="schwach" style={{ fontSize: 11 }}>
                {qd.anzahl.toLocaleString('de-DE')}
              </span>
            </div>

            {qdAuf && geschosse.map((g) => {
              const gAuf = offen.has(`g:${qd.schluessel}:${g.schluessel}`)
                        || suche.trim().length > 0;

              return (
                <Fragment key={g.schluessel}>
                  {/* --- Geschoss ------------------------------------------ */}
                  <div
                    className="baumzeile"
                    style={{ display: 'flex', gap: 6, alignItems: 'center',
                             padding: '4px 4px 4px 18px', cursor: 'pointer' }}
                    onClick={() => umschalten(`g:${qd.schluessel}:${g.schluessel}`)}
                  >
                    <span className="sehr-schwach" style={{ fontSize: 10, width: 9 }}>
                      {gAuf ? '−' : '+'}
                    </span>
                    <IconEbene size={12} />
                    <span style={{ fontSize: 12 }}>{g.name}</span>
                    <span style={{ flex: 1 }} />
                    <span className="schwach" style={{ fontSize: 11 }}>
                      {g.anzahl.toLocaleString('de-DE')}
                    </span>
                  </div>

                  {gAuf && g.verteiler.map((v) => {
                    const vAuf = offen.has(`v:${v.id}`);

                    return (
                      <Fragment key={v.id}>
                        {/* --- Verteiler ---------------------------------- */}
                        <div
                          className="baumzeile"
                          style={{
                            display: 'flex', gap: 6, alignItems: 'center',
                            padding: '3px 4px 3px 36px', cursor: 'pointer',
                            background: auswahl?.schluessel === v.id
                              ? 'var(--flaeche, #f0f0f0)' : undefined,
                          }}
                          onClick={() => {
                            umschalten(`v:${v.id}`);
                            waehlen({
                              art: 'verteiler',
                              schluessel: v.id,
                              name: v.bezeichnung
                                ? `${v.schluessel} · ${v.bezeichnung}`
                                : v.schluessel,
                              filter: { verteilerId: v.id },
                            });
                          }}
                          title={v.bezeichnung ?? undefined}
                        >
                          <span className="sehr-schwach"
                                style={{ fontSize: 10, width: 9 }}>
                            {v.kreise.length > 0 || v.ohneKreis > 0
                              ? (vAuf ? '−' : '+') : ''}
                          </span>

                          <span style={{ fontSize: 12 }}>
                            {/* Die Nummer voran — danach wird gesucht. */}
                            {v.verteilernummer
                              ? `UV ${v.verteilernummer}`
                              : v.schluessel}
                          </span>

                          {v.verteilernummer && (
                            <span className="sehr-schwach" style={{ fontSize: 10 }}>
                              {v.schluessel}
                            </span>
                          )}

                          {v.einspeisung && (
                            <span className="sehr-schwach" style={{ fontSize: 10 }}
                                  title="Einspeisung">
                              ← {v.einspeisung}
                            </span>
                          )}

                          {/* Bauteile, deren Quadrant nicht zum Bereich passt.
                              Entweder ist das Bauteil falsch gesetzt oder der
                              Raum dem falschen Bereich zugeordnet. */}
                          {v.fremdeQuadranten.length > 0 && (
                            <span className="marke warnung" style={{ fontSize: 9 }}
                                  title={`Bauteile aus Quadrant `
                                       + `${v.fremdeQuadranten.join(', ')} `
                                       + `hängen an diesem Verteiler`}>
                              Q{v.fremdeQuadranten.join('/')}
                            </span>
                          )}

                          {!v.verteilerbereich && (
                            <span className="sehr-schwach" style={{ fontSize: 9 }}
                                  title="Ohne Verteilerbereich bleibt er ohne Bauteile">
                              kein Bereich
                            </span>
                          )}

                          <span style={{ flex: 1 }} />
                          <span className="schwach" style={{ fontSize: 11 }}>
                            {v.anzahl}
                          </span>
                        </div>

                        {vAuf && (
                          <>
                            {v.kreise.map((k) => (
                              <div
                                key={k.id}
                                className="baumzeile"
                                style={{
                                  display: 'flex', gap: 6, alignItems: 'center',
                                  padding: '2px 4px 2px 56px', cursor: 'pointer',
                                  background: auswahl?.schluessel === k.id
                                    ? 'var(--flaeche, #f0f0f0)' : undefined,
                                }}
                                onClick={() => waehlen({
                                  art: 'verteiler',
                                  schluessel: k.id,
                                  name: `${v.schluessel} · ${k.name}`,
                                  filter: { stromkreisId: k.id },
                                })}
                              >
                                <span style={{ fontSize: 11 }}>{k.schluessel}</span>

                                {k.istSicherheit && (
                                  <span className="marke warnung"
                                        style={{ fontSize: 9 }}
                                        title="Sicherheitsstromversorgung">SV</span>
                                )}

                                {k.schutzorgan && (
                                  <span className="sehr-schwach"
                                        style={{ fontSize: 10 }}>
                                    {k.schutzorgan}
                                  </span>
                                )}

                                <span style={{ flex: 1 }} />
                                <span className="schwach" style={{ fontSize: 11 }}>
                                  {k.anzahl}
                                </span>
                              </div>
                            ))}

                            {v.ohneKreis > 0 && (
                              <div
                                className="baumzeile"
                                style={{
                                  display: 'flex', gap: 6, alignItems: 'center',
                                  padding: '2px 4px 2px 56px', cursor: 'pointer',
                                }}
                                onClick={() => waehlen({
                                  art: 'verteiler',
                                  schluessel: `${v.id}-ohne`,
                                  name: `${v.schluessel} · ohne Abgang`,
                                  filter: { verteilerId: v.id },
                                })}
                              >
                                <span className="sehr-schwach"
                                      style={{ fontSize: 11, fontStyle: 'italic' }}>
                                  ohne Abgang
                                </span>
                                <span style={{ flex: 1 }} />
                                <span className="schwach" style={{ fontSize: 11 }}>
                                  {v.ohneKreis}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </Fragment>
        );
      })}

      {(daten?.ohneVerteiler ?? 0) > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8,
                      borderTop: '1px solid var(--linie, #eee)' }}>
          <div className="baumzeile"
               style={{ display: 'flex', gap: 6, padding: '4px' }}>
            <IconWarnung size={12} />
            <span style={{ fontSize: 12 }}>ohne Verteiler</span>
            <span style={{ flex: 1 }} />
            <span className="schwach" style={{ fontSize: 11 }}>
              {(daten?.ohneVerteiler ?? 0).toLocaleString('de-DE')}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// --- Die Typensicht ---------------------------------------------------------

interface TypKnoten {
  schluessel: string;
  name: string;
  anzahl: number;
  kinder: {
    schluessel: string;
    name: string;
    anzahl: number;
    nummern: { nummer: string; anzahl: number }[];
  }[];
}

function TypBaum({
  daten, suche, auswahl, waehlen,
}: {
  daten: TypKnoten[];
  suche: string;
  auswahl: Auswahl | null;
  waehlen: (a: Auswahl) => void;
}) {
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const s = suche.toLowerCase();

  return (
    <>
      {(daten ?? []).map((g) => {
        const reihen = suche.trim()
          ? g.kinder.filter((r) => r.name.toLowerCase().includes(s))
          : g.kinder;

        if (suche.trim() && reihen.length === 0) return null;

        const auf = offen.has(g.schluessel) || suche.trim().length > 0;

        return (
          <Fragment key={g.schluessel}>
            <div
              className="baumzeile"
              style={{ display: 'flex', gap: 6, alignItems: 'center',
                       padding: '4px', cursor: 'pointer' }}
              onClick={() => {
                const n = new Set(offen);
                auf ? n.delete(g.schluessel) : n.add(g.schluessel);
                setOffen(n);
              }}
              title={g.schluessel === 'EE1' ? 'Allgemeinbeleuchtung'
                   : g.schluessel === 'EE2' ? 'Sicherheitsbeleuchtung' : undefined}
            >
              <span className="sehr-schwach" style={{ fontSize: 10, width: 9 }}>
                {reihen.length > 0 ? (auf ? '−' : '+') : ''}
              </span>

              <span style={{ fontSize: 12, fontWeight: 500 }}>{g.name}</span>

              {/* Die Bedeutung der Vorsilbe. Sie steht nirgends im Modell und
                  ergibt sich aus der Auswertung — deshalb hier angeschrieben. */}
              {g.schluessel === 'EE1' && (
                <span className="sehr-schwach" style={{ fontSize: 10 }}>
                  Allgemeinbeleuchtung
                </span>
              )}
              {g.schluessel === 'EE2' && (
                <span className="sehr-schwach" style={{ fontSize: 10 }}>
                  Sicherheitsbeleuchtung
                </span>
              )}

              <span style={{ flex: 1 }} />
              <span className="schwach" style={{ fontSize: 11 }}>
                {g.anzahl.toLocaleString('de-DE')}
              </span>
            </div>

            {auf && reihen.map((r) => (
              <Fragment key={r.schluessel}>
                <div
                  className="baumzeile"
                  style={{
                    display: 'flex', gap: 6, alignItems: 'center',
                    padding: '3px 4px 3px 27px', cursor: 'pointer',
                    background: auswahl?.schluessel === r.schluessel
                      ? 'var(--flaeche, #f0f0f0)' : undefined,
                  }}
                  onClick={() => waehlen({
                    art: 'typ',
                    schluessel: r.schluessel,
                    name: `Typenreihe ${r.name}`,
                    // Die Reihe fasst mehrere Nummern — gefiltert wird auf die
                    // erste, weil die Schnittstelle eine Nummer nimmt.
                    filter: { typennummer: r.nummern[0]?.nummer },
                  })}
                >
                  <span style={{ fontSize: 12 }}>{r.name}</span>
                  <span style={{ flex: 1 }} />
                  <span className="schwach" style={{ fontSize: 11 }}>
                    {r.anzahl}
                  </span>
                </div>

                {/* Die einzelnen Nummern nur, wenn die Reihe mehrere hat. Bei
                    einer wäre die Zeile eine Wiederholung. */}
                {r.nummern.length > 1 && r.nummern.map((n) => (
                  <div
                    key={n.nummer}
                    className="baumzeile"
                    style={{
                      display: 'flex', gap: 6, alignItems: 'center',
                      padding: '2px 4px 2px 46px', cursor: 'pointer',
                      background: auswahl?.schluessel === n.nummer
                        ? 'var(--flaeche, #f0f0f0)' : undefined,
                    }}
                    onClick={() => waehlen({
                      art: 'typ',
                      schluessel: n.nummer,
                      name: `Typ ${n.nummer}`,
                      filter: { typennummer: n.nummer },
                    })}
                  >
                    <span className="schwach" style={{ fontSize: 11 }}>
                      {n.nummer}
                    </span>
                    <span style={{ flex: 1 }} />
                    <span className="sehr-schwach" style={{ fontSize: 10 }}>
                      {n.anzahl}
                    </span>
                  </div>
                ))}
              </Fragment>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

// === Der Inhalt ==============================================================

function Inhalt({
  api, projektId, auswahl, arten,
}: {
  api: ApiClient;
  projektId: string;
  auswahl: Auswahl;
  arten: Record<string, Elektrobauteilart>;
}) {
  const [seite, setSeite] = useState<Elektroseite | null>(null);
  const [nummer, setNummer] = useState(1);
  const [offenesBauteil, setOffenesBauteil] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setNummer(1);
    setOffenesBauteil(null);
  }, [auswahl.schluessel]);

  useEffect(() => {
    let abgebrochen = false;

    api.elektroBauteile(projektId, { ...auswahl.filter, seite: nummer,
                                     proSeite: 100 })
      .then((s) => { if (!abgebrochen) { setSeite(s); setFehler(null); } })
      .catch((e) => {
        if (!abgebrochen) setFehler(e instanceof Error ? e.message : String(e));
      });

    return () => { abgebrochen = true; };
  }, [projektId, auswahl.schluessel, nummer]);

  if (fehler) return <div className="hinweis fehler">{fehler}</div>;
  if (!seite) return <div className="leer">wird geladen …</div>;

  // Nach Bauteilart gruppieren — wie bei Heizung. Die Liste kommt bereits
  // sortiert; die Zwischenzeile macht den Wechsel nur sichtbar.
  const gruppen: { art: string; name: string;
                   bauteile: typeof seite.bauteile }[] = [];

  for (const b of seite.bauteile) {
    let g = gruppen.find((x) => x.art === b.bauteilart);

    if (!g) {
      g = {
        art: b.bauteilart,
        name: arten[b.bauteilart]?.bezeichnung ?? b.bauteilart,
        bauteile: [],
      };

      gruppen.push(g);
    }

    g.bauteile.push(b);
  }

  const seiten = Math.ceil(seite.gesamt / seite.proSeite);

  // TV11e: die Raumnummern, die mehrere Räume tragen - ohne Rücksicht auf Groß- und Kleinschreibung wie die API.
  const mehrdeutig = new Set((seite.raeumeMehrdeutig ?? []).map((n) => n.toUpperCase()));

  return (
    <>
      <h3 style={{ marginTop: 0, fontSize: 14, fontWeight: 500 }}>
        {auswahl.name}
        <span className="schwach" style={{ marginLeft: 8, fontSize: 12 }}>
          {seite.gesamt.toLocaleString('de-DE')} Bauteil(e)
        </span>
      </h3>

      <div className="tabellenrahmen">
        <table className="zuordnung" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Kennzeichen</th>
              <th style={{ textAlign: 'left' }}>Typ</th>
              <th style={{ textAlign: 'left' }}>Raum</th>
              <th style={{ textAlign: 'center' }}>Qu</th>
              <th style={{ textAlign: 'left' }}>Verteiler</th>
              <th style={{ textAlign: 'left' }}>Abgang</th>
              <th style={{ textAlign: 'left' }}>Daten</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {gruppen.map((g) => (
              <Fragment key={g.art}>
                <tr>
                  <td colSpan={8} style={{
                    background: 'var(--flaeche, #f5f5f5)',
                    fontWeight: 500, fontSize: 12, padding: '5px 8px',
                    borderTop: '1px solid var(--linie, #ddd)',
                    // Ein Farbstreifen links, in der Farbe der Anlagengruppe.
                    // Er ordnet die Gruppen beim Ueberfliegen ein, ohne die
                    // Kostengruppe lesen zu muessen.
                    borderLeft: `3px solid ${farbeFuer(
                      g.bauteile[0]?.kostengruppe ?? null)}`,
                  }}>
                    {(() => {
                      const S = symbolFuer(g.art);
                      return <S size={15} />;
                    })()}
                    <span style={{ marginLeft: 6 }}>{g.name}</span>
                    <span className="sehr-schwach" style={{ marginLeft: 8,
                                                            fontSize: 11 }}>
                      {g.bauteile.length}
                    </span>
                  </td>
                </tr>

                {g.bauteile.map((b) => (
                  <Bauteilzeile
                    key={b.id}
                    b={b}
                    api={api}
                    verteiler={seite.verteiler}
                    stromkreise={seite.stromkreise}
                    raeume={seite.raeume}
                    mehrdeutig={mehrdeutig}
                    offen={offenesBauteil === b.id}
                    umschalten={() => setOffenesBauteil(
                      offenesBauteil === b.id ? null : b.id)}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {seiten > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                      marginTop: 10, fontSize: 12 }}>
          <button onClick={() => setNummer(Math.max(1, nummer - 1))}
                  disabled={nummer <= 1} style={{ fontSize: 11 }}>
            zurück
          </button>
          <span className="schwach">Seite {nummer} von {seiten}</span>
          <button onClick={() => setNummer(Math.min(seiten, nummer + 1))}
                  disabled={nummer >= seiten} style={{ fontSize: 11 }}>
            weiter
          </button>
        </div>
      )}
    </>
  );
}

// === Eine Bauteilzeile =======================================================

/** Exportiert für den Test der Raumspalte (TV11e). */
export function Bauteilzeile({
  b, api, verteiler, stromkreise, raeume, mehrdeutig, offen, umschalten,
}: {
  b: Elektroseite['bauteile'][number];
  api: ApiClient;
  verteiler: Elektroseite['verteiler'];
  stromkreise: Elektroseite['stromkreise'];
  raeume: Elektroseite['raeume'];
  /** TV11e: Raumnummern mehrfach im Raumbuch, in Großbuchstaben (`Elektroseite.raeumeMehrdeutig`). */
  mehrdeutig: ReadonlySet<string>;
  offen: boolean;
  umschalten: () => void;
}) {
  const [detail, setDetail] = useState<Elektrobauteil | null>(null);

  const Sym = symbolFuer(b.bauteilart);

  // Aus den Verzeichnissen der Seite auflösen.
  const v = b.verteilerId ? verteiler[b.verteilerId] : undefined;
  const k = b.stromkreisId ? stromkreise[b.stromkreisId] : undefined;
  const raumname = b.raumnummer ? raeume[b.raumnummer]?.name : undefined;
  const raumMehrdeutig = !!b.raumnummer && mehrdeutig.has(b.raumnummer.toUpperCase());

  useEffect(() => {
    if (!offen || detail) return;

    api.elektroBauteil(b.id).then(setDetail).catch(() => setDetail(null));
  }, [offen]);

  return (
    <>
      <tr onClick={umschalten} style={{ cursor: 'pointer' }}>
        <td className="zuordnung-name" style={{ fontSize: 12, paddingLeft: 8 }}>
          {/* Das Sinnbild der Bauteilart. Bei dreihundert Zeilen findet das Auge
              die Leuchten, ohne zu lesen. */}
          <Sym size={15} title={b.bauteilart} />
          <span style={{ marginLeft: 6 }}>
            {b.kennzeichen ?? b.bezeichnung ?? '—'}
          </span>
        </td>

        <td className="schwach" style={{ fontSize: 11 }}>{b.typ ?? '—'}</td>

        {/* Der Raum mit seiner Bezeichnung.
            Der Name kommt aus dem Raumbuch, nicht vom Bauteil: Das Raumbuch ist
            die gepflegte Quelle. Wo es den Raum nicht kennt, steht der Name aus
            dem Modell — und ein Vermerk, dass er nicht im Raumbuch ist. */}
        <td className="schwach" style={{ fontSize: 11 }}>
          {b.raumnummer ? (
            <>
              <span style={{ fontWeight: 500 }}>{b.raumnummer}</span>
              {' '}
              {/* TV11e: Eine Nummer, die mehrere Räume tragen, steht im Raumbuch - nur nicht eindeutig. Bis TV11e hieß
                  sie hier „nicht im Raumbuch“. */}
              {raumname ?? (raumMehrdeutig ? (
                <span className="sehr-schwach" title="Raumnummer mehrfach im Raumbuch">
                  {b.raumname ?? ''} <em>(Raumnummer mehrfach im Raumbuch)</em>
                </span>
              ) : (
                <span className="sehr-schwach" title="nicht im Raumbuch">
                  {b.raumname ?? ''} <em>(nicht im Raumbuch)</em>
                </span>
              ))}
            </>
          ) : (
            <span className="sehr-schwach">kein Raum</span>
          )}
        </td>

        {/* Der Quadrant — bei einer Arena die gröbste Ordnung. */}
        <td style={{ textAlign: 'center', fontSize: 11 }} className="schwach">
          {b.quadrant ?? <span className="sehr-schwach">—</span>}
        </td>

        <td className="schwach" style={{ fontSize: 11 }}>
          {v ? (
            <>
              <span style={{ fontWeight: 500 }}>{v.kennzeichen}</span>
              {b.kreisHerkunft === 'hand' && (
                <span className="sehr-schwach" style={{ fontSize: 9, marginLeft: 4 }}
                      title="von Hand gesetzt — wird nicht überschrieben">
                  fest
                </span>
              )}
            </>
          ) : (
            <span className="sehr-schwach">—</span>
          )}
        </td>

        <td className="schwach" style={{ fontSize: 11 }}>
          {k ? (
            <>
              {k.kennzeichen}
              {k.schutzorgan && (
                <span className="sehr-schwach" style={{ marginLeft: 4 }}>
                  {k.schutzorgan}
                </span>
              )}
              {k.istSicherheit && (
                <span className="marke warnung" style={{ fontSize: 9, marginLeft: 4 }}
                      title="Sicherheitsstromversorgung">SV</span>
              )}
            </>
          ) : (
            <span className="sehr-schwach">—</span>
          )}
        </td>

        <td style={{ fontSize: 11 }}>
          {b.datenstand === 'zugewiesen' && (
            <span className="marke gut" style={{ fontSize: 9 }}>Daten</span>
          )}
          {b.datenstand === 'pruefen' && (
            <span className="marke warnung" style={{ fontSize: 9 }}
                  title={b.datenstandGrund ?? undefined}>prüfen</span>
          )}
          {b.datenstand === 'offen' && (
            <span className="sehr-schwach" style={{ fontSize: 9 }}>offen</span>
          )}
        </td>

        <td style={{ textAlign: 'right' }}>
          <span className="sehr-schwach" style={{ fontSize: 10 }}>
            {offen ? '−' : '+'}
          </span>
        </td>
      </tr>

      {offen && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--flaeche, #fafafa)',
                                   padding: '10px 18px' }}>
            {!detail ? (
              <div className="leer" style={{ fontSize: 12 }}>wird geladen …</div>
            ) : (
              <Bauteildetail d={detail} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Bauteildetail({ d }: { d: Elektrobauteil }) {
  return (
    <>
      {d.datenstandGrund && (
        <div className="hinweis" style={{ marginBottom: 10 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 12 }}>{d.datenstandGrund}</div>
        </div>
      )}

      {(d.datenblattUrl || d.herstellerUrl) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 12 }}>
          {d.datenblattUrl && (
            <a href={d.datenblattUrl} target="_blank" rel="noreferrer">
              Datenblatt öffnen
            </a>
          )}
          {d.herstellerUrl && (
            <a href={d.herstellerUrl} target="_blank" rel="noreferrer">
              Herstellerseite
            </a>
          )}
          <span className="sehr-schwach" style={{ fontSize: 10 }}>
            ohne Anmeldung erreichbar
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap',
                    marginBottom: 12, fontSize: 12 }}>
        <Feld name="Raum"
              wert={d.raumnummer
                ? `${d.raumnummer}${d.raumname ? '  ' + d.raumname : ''}`
                : null} />
        <Feld name="Quadrant" wert={d.quadrant} />
        <Feld name="Ebene" wert={d.geschoss} />
        <Feld name="Familie" wert={d.familie} />
        <Feld name="Typ" wert={d.typ} />
        <Feld name="Bereich" wert={d.bearbeitungsbereich} />
        <Feld name="Verteiler"
              wert={d.verteiler
                ? `${d.verteiler.kennzeichen}${
                    d.verteiler.bezeichnung ? '  ' + d.verteiler.bezeichnung : ''}`
                : null} />
        <Feld name="Abgang"
              wert={d.stromkreis
                ? `${d.stromkreis.kennzeichen}${
                    d.stromkreis.schutzorgan ? '  ' + d.stromkreis.schutzorgan : ''}`
                : null} />
      </div>

      {/* Wo der Modellparameter von der Lage abweicht.
          Jede solche Zeile ist ein Bauteil, dessen Raumangabe nicht zur Geometrie
          passt - entweder falsch gesetzt oder nicht gepflegt. */}
      {d.werte.some((w) => w.feldschluessel === 'raum_laut_modell') && (
        <div className="hinweis" style={{ marginBottom: 10 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 12 }}>
            Der Modellparameter nennt
            {' '}<strong>
              {d.werte.find((w) => w.feldschluessel === 'raum_laut_modell')?.wert}
            </strong>, die Lage aber
            {' '}<strong>{d.raumnummer ?? 'keinen Raum'}</strong>.
            {' '}Entweder ist das Bauteil falsch gesetzt oder der Parameter nicht
            gepflegt.
          </div>
        </div>
      )}

      {/* Die Typwerte getrennt von den Bauteilwerten.
          Sie gelten für alle Exemplare des Fabrikats und sind hier nicht
          änderbar — wer sie ändert, ändert das Fabrikat. */}
      {d.typwerte.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="feldname" style={{ marginBottom: 4 }}>
            Werte des Fabrikats
            <span className="sehr-schwach" style={{ marginLeft: 6, fontSize: 10 }}>
              gelten für alle Exemplare · Pflege in der Datenpflege
            </span>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap',
                        fontSize: 12 }}>
            {d.typwerte.map((w) => (
              <div key={w.feldschluessel}
                   style={{ background: 'var(--hellgelb, #fffbe6)',
                            padding: '2px 6px', borderRadius: 2 }}>
                <span className="sehr-schwach" style={{ fontSize: 10 }}>
                  {w.feldschluessel}
                </span>
                <div>{w.wert}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.werte.length > 0 && (
        <div>
          <div className="feldname" style={{ marginBottom: 4 }}>
            Werte am Bauteil
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap',
                        fontSize: 12 }}>
            {d.werte.map((w) => (
              <div key={w.feldschluessel}
                   style={{
                     background: w.herkunft === 'modell'
                       ? 'var(--hellgruen, #f0fff4)'
                       : 'var(--hellgelb, #fffbe6)',
                     padding: '2px 6px', borderRadius: 2,
                   }}>
                <span className="sehr-schwach" style={{ fontSize: 10 }}>
                  {w.feldschluessel}
                </span>
                <div>{w.wert}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.werte.length === 0 && d.typwerte.length === 0 && (
        <div className="sehr-schwach" style={{ fontSize: 12 }}>
          Noch keine Werte. Sie entstehen beim Auslesen aus dem Modell und in der
          Datenpflege am Fabrikat.
        </div>
      )}
    </>
  );
}

function Feld({ name, wert }: { name: string; wert: string | null }) {
  if (!wert) return null;

  return (
    <div>
      <div className="feldname" style={{ fontSize: 10 }}>{name}</div>
      <div>{wert}</div>
    </div>
  );
}

// === Die Legende =============================================================

function Legende() {
  return (
    <div className="karte">
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11 }}>
        <span>
          <span style={{ background: 'var(--hellgruen, #f0fff4)',
                         padding: '1px 8px', marginRight: 5 }} />
          aus dem Modell gelesen
        </span>
        <span>
          <span style={{ background: 'var(--hellgelb, #fffbe6)',
                         padding: '1px 8px', marginRight: 5 }} />
          nur in der Datenbank
        </span>
        <span className="schwach">
          <strong>Lage</strong> — Raum aus der Lage bestimmt, nicht im Modell
          zugeordnet
        </span>
        <span className="schwach">
          <strong>fest</strong> — Verteiler von Hand gesetzt, wird nicht
          überschrieben
        </span>
        <span className="schwach">
          Die Sinnbilder sind nach Anlagengruppe abgestuft — dunkel für
          Starkstrom, hell für Schwachstrom
        </span>
      </div>
    </div>
  );
}
